import { C } from '../data/constants.js';

// Power-up types
export const PU_TYPES = {
  MULTI_BALL:  0,
  FIREBALL:    1,
  WIDE_PADDLE: 2,
  LASER:       3,
  MAGNET:      4,
  EMP:         5,
  SHIELD:      6,
  SPEED_DOWN:  7,
};

// Visual config per type: [color, icon text, name, secondary color]
export const PU_CONFIG = [
  { color: '#ffff00', secColor: '#ff8800', icon: 'x3',  name: 'Multi-Ball' },
  { color: '#ff4400', secColor: '#ffbb00', icon: '\u{1F525}',  name: 'Fireball' },    // 🔥
  { color: '#00ff66', secColor: '#00ffff', icon: '\u2194', name: 'Wide Paddle' },  // ↔
  { color: '#4488ff', secColor: '#88ffff', icon: '\u26A1', name: 'Laser' },        // ⚡
  { color: '#cc44ff', secColor: '#ff88ff', icon: '\u{1F9F2}',  name: 'Magnet' },       // 🧲
  { color: '#ffffff', secColor: '#88ccff', icon: '\u{1F4A5}',  name: 'EMP' },          // 💥
  { color: '#ff8800', secColor: '#ffff44', icon: '\u{1F6E1}',  name: 'Shield' },       // 🛡
  { color: '#44ddff', secColor: '#0088ff', icon: '\u25BC', name: 'Slow' },         // ▼
];

export class PowerUp {
  constructor(x, y, type) {
    this.x = x;
    this.y = y;
    this.type = type;
    this.w = C.POWERUP_W + 4; // slightly chunkier capsule for extra presence
    this.h = C.POWERUP_H + 2;
    this.alive = true;
    this.config = PU_CONFIG[type];
    this.bobPhase = Math.random() * Math.PI * 2;
    this.orbitAngle = Math.random() * Math.PI * 2;
    this.swayAngle = 0;
    this.particles = []; // trailing spark motes
  }

  update() {
    this.y += C.POWERUP_FALL_SPEED;
    this.bobPhase += 0.08;
    this.orbitAngle += 0.12;
    this.swayAngle = Math.sin(this.bobPhase) * 0.08; // subtle pendulum tilt

    // Spawn trailing energy spark
    if (Math.random() < 0.35) {
      this.particles.push({
        x: this.x + (Math.random() - 0.5) * (this.w * 0.6),
        y: this.y + (Math.random() - 0.5) * 4,
        size: Math.random() * 2 + 1,
        life: 18,
        maxLife: 18,
        vx: (Math.random() - 0.5) * 0.4,
        vy: -0.4 - Math.random() * 0.5
      });
    }

    // Update trailing particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.life--;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
    }
    
    // Off screen — missed
    if (this.y > C.CANVAS_H + 25) {
      this.alive = false;
    }
  }

  // Check collision with paddle
  hitsPaddle(paddle) {
    return (
      this.x + this.w / 2 > paddle.left &&
      this.x - this.w / 2 < paddle.right &&
      this.y + this.h / 2 > paddle.top &&
      this.y - this.h / 2 < paddle.bottom
    );
  }

  render(ctx) {
    if (!this.alive) return;

    ctx.save();
    
    const bobOffset = Math.sin(this.bobPhase) * 2;
    const drawX = this.x;
    const drawY = this.y + bobOffset;

    // 1. Render Falling Sparkle Trail
    for (const p of this.particles) {
      const alpha = p.life / p.maxLife;
      ctx.fillStyle = this.config.color;
      ctx.globalAlpha = alpha * 0.7;
      ctx.shadowColor = this.config.color;
      ctx.shadowBlur = 4;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * alpha, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;

    // Apply gentle tilt transform
    ctx.translate(drawX, drawY);
    ctx.rotate(this.swayAngle);

    const halfW = this.w / 2;
    const halfH = this.h / 2;
    const r = 5;

    // 2. Pulsing Outer Aura Halo
    const auraPulse = 0.5 + Math.sin(this.bobPhase * 2) * 0.25;
    ctx.strokeStyle = this.config.color;
    ctx.globalAlpha = auraPulse * 0.4;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(-halfW - 2, -halfH - 2, this.w + 4, this.h + 4, r + 2);
    ctx.stroke();

    // 3. Main Glass Capsule Body with Gradient Core
    ctx.globalAlpha = 1;
    ctx.shadowColor = this.config.color;
    ctx.shadowBlur = 12;

    const capsuleGrad = ctx.createLinearGradient(0, -halfH, 0, halfH);
    capsuleGrad.addColorStop(0, '#ffffff');
    capsuleGrad.addColorStop(0.3, this.config.color);
    capsuleGrad.addColorStop(0.8, this.config.secColor || this.config.color);
    capsuleGrad.addColorStop(1, '#110022');

    ctx.fillStyle = capsuleGrad;
    ctx.beginPath();
    ctx.roundRect(-halfW, -halfH, this.w, this.h, r);
    ctx.fill();

    // Metallic / Neon Rim
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // 4. Glossy Specular Glass Reflection (top curve)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
    ctx.beginPath();
    ctx.roundRect(-halfW + 1.5, -halfH + 1.5, this.w - 3, this.h * 0.38, [r - 1, r - 1, 1, 1]);
    ctx.fill();

    // 5. Icon Badge / Text Symbol
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 9px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.config.icon, 0, 0.5);

    // 6. 3D Orbital Energy Electrons / Photon Ring
    const orbitRadiusX = halfW + 6;
    const orbitRadiusY = halfH + 2;

    const ox1 = Math.cos(this.orbitAngle) * orbitRadiusX;
    const oy1 = Math.sin(this.orbitAngle) * orbitRadiusY * 0.4;
    const ox2 = Math.cos(this.orbitAngle + Math.PI) * orbitRadiusX;
    const oy2 = Math.sin(this.orbitAngle + Math.PI) * orbitRadiusY * 0.4;

    // Orbit Particle 1
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = this.config.color;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(ox1, oy1, 2, 0, Math.PI * 2);
    ctx.fill();

    // Orbit Particle 2
    ctx.fillStyle = this.config.color;
    ctx.beginPath();
    ctx.arc(ox2, oy2, 1.8, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }
}
